import React, { useState } from 'react';
import { Video, Trash2, Pencil, Star } from 'lucide-react';
import VideoUploadForm from './VideoUploadForm';
import VideoEditModal from './VideoEditModal';
import InstagramImportPanel from './InstagramImportPanel';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminVideoCard = ({ video, onEdit, onDelete }) => (
  <div className="bg-black/50 border border-red-900/20 rounded-lg overflow-hidden" data-testid={`admin-video-${video.id}`}>
    <div className="aspect-video bg-zinc-800 relative">
      <video src={video.video_url} className="w-full h-full object-cover" preload="metadata" />
      {video.is_exclusive && (
        <div
          className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded-full bg-gradient-to-r from-yellow-500 to-amber-400 text-black shadow-[0_0_12px_rgba(250,204,21,0.6)]"
          data-testid="admin-exclusive-badge"
        >
          <Star className="h-3 w-3 fill-black" />
          <span className="text-[10px] font-black tracking-widest">EXCLUSIVE</span>
        </div>
      )}
    </div>
    <div className="p-4">
      <h4 className="text-white font-bold mb-1 text-sm line-clamp-1">{video.title}</h4>
      <p className="text-gray-400 text-xs mb-2 line-clamp-2">{video.description}</p>
      <p className="text-gray-600 text-xs mb-3">
        {new Date(video.uploaded_at).toLocaleDateString()}
      </p>
      <div className="flex gap-2">
        <button
          onClick={() => onEdit(video)}
          data-testid={`edit-video-${video.id}`}
          className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white font-bold py-2 px-3 rounded text-xs transition-colors flex items-center justify-center gap-2"
        >
          <Pencil className="h-3 w-3" />
          Edit
        </button>
        <button
          onClick={() => onDelete(video.id)}
          data-testid={`delete-video-${video.id}`}
          className="flex-1 bg-red-900/50 hover:bg-red-900/70 text-red-400 font-bold py-2 px-3 rounded text-xs transition-colors flex items-center justify-center gap-2"
        >
          <Trash2 className="h-3 w-3" />
          Delete
        </button>
      </div>
    </div>
  </div>
);

const VideosTab = ({ token, videos, onRefresh }) => {
  const [editingVideo, setEditingVideo] = useState(null);

  const handleDelete = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video? This will also remove it from R2.')) return;
    try {
      const res = await fetch(`${API}/api/videos/${videoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        onRefresh();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Failed to delete video');
      }
    } catch {
      alert('Error deleting video');
    }
  };

  return (
    <div data-testid="videos-tab">
      <InstagramImportPanel token={token} onImported={onRefresh} />
      <VideoUploadForm token={token} onUploaded={onRefresh} />

      <div className="bg-zinc-900/80 backdrop-blur-xl border border-red-900/30 rounded-lg p-6">
        <h3 className="text-xl font-black text-white mb-4">Uploaded Videos ({videos.length})</h3>

        {videos.length === 0 ? (
          <div className="text-center py-8">
            <Video className="h-12 w-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500">No videos uploaded yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {videos.map((video) => (
              <AdminVideoCard
                key={video.id}
                video={video}
                onEdit={setEditingVideo}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      {editingVideo && (
        <VideoEditModal
          video={editingVideo}
          token={token}
          onClose={() => setEditingVideo(null)}
          onSaved={() => {
            setEditingVideo(null);
            onRefresh();
          }}
        />
      )}
    </div>
  );
};

export default VideosTab;
